import React, { MutableRefObject, useImperativeHandle } from 'react';
import { Nullable } from 'global';
import { Add } from '@mui/icons-material';
import { FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Listing, ListingForm, useListing } from 'components/shared/TableListing';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { StagesFormValues } from '../../../types';
import { stagesConstructorFormRow } from '../../../constants';
import { StagesConstructorListingSchema } from '../../../model';
import { AttributeCell, TitleCell, WinProbabilityCell } from './cells';

interface Props {
  isEdit: boolean;
  initialValues: StagesFormValues[];
  submitRef: MutableRefObject<{ submit: () => Promise<Nullable<ListingForm<StagesFormValues>>> } | null>;
}

export const StagesConstructorListing = ({ isEdit, submitRef, initialValues }: Props) => {
  const { form, columns, tableData, handleAddRow, handleRemoveRow, handleMoveRow } = useListing<StagesFormValues>(
    {
      headerColumns: [
        {
          header: 'Stage Name',
          renderKey: 'name',
          renderCell: ({ rowIndex }) => (
            <TitleCell
              isEdit={isEdit}
              rowIndex={rowIndex}
              onMoveRow={handleMoveRow}
              onRemove={() => {
                handleRemoveRow(rowIndex);
              }}
            />
          ),
        },
        {
          header: 'Stage Attributes',
          renderKey: 'checklist',
          renderCell: ({ rowIndex }) => <AttributeCell isEdit={isEdit} rowIndex={rowIndex} prefix="listOfAttributes" />,
        },
        {
          header: 'Win Probability',
          renderKey: 'winProbability',
          renderCell: ({ rowIndex }) => <WinProbabilityCell isEdit={isEdit} rowIndex={rowIndex} />,
        },
      ],
      bodyFields: initialValues,
    },
    {
      mode: 'onChange',
      shouldUnregister: false,
      resolver: zodResolver(StagesConstructorListingSchema),
    }
  );

  useImperativeHandle(submitRef, () => ({
    submit: async () => {
      const isValid = await form.trigger();
      return isValid ? form.getValues() : null;
    },
  }));

  return (
    <Stack gap="1rem">
      <Typography fontWeight={500}>Stage Settings</Typography>

      <FormProvider {...form}>
        <Listing<StagesFormValues>
          columns={columns}
          tableData={tableData}
          actionPanelBottom={() => (
            <RenderWithCondition condition={!isEdit}>
              <Box>
                <Button
                  startIcon={<Add />}
                  onClick={() => {
                    handleAddRow({ ...stagesConstructorFormRow });
                  }}
                >
                  Add stage
                </Button>
              </Box>
            </RenderWithCondition>
          )}
        />
      </FormProvider>
    </Stack>
  );
};
